import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { benchmarkService, promptService, domainService, schemaService, groundTruthService, ocrConfigService, llmConfigService } from '../../services';
import { Domain, Prompt, ExtractionSchema, GroundTruthDocument, OCRConfig, LLMConfig } from '../../types';
import { DomainSelector } from '../shared/DomainSelector';
import { Modal, FilterableTable } from '../ui';
import type { FilterableColumnDef } from '../ui';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (runId: string) => void;
  preSelectedSchemaId?: string;
}

/**
 * Détermine l'id à présélectionner pour une dimension (prompt/schéma/OCR/LLM) :
 * la config ACTIVE du client courant pour ce domaine (endpoint /tenant/me/.../active,
 * la même que l'extraction prod via ConfigResolver). Fallback sur le 1er élément de
 * la liste si aucune config active (404) ou si l'active n'est pas dans la liste
 * (ex. dépréciée). Renvoie '' si la liste est vide.
 */
async function pickActiveId<T extends { id: string }>(
  domain: string,
  list: T[],
  getActive: (domain: string) => Promise<{ id: string }>,
): Promise<{ selectedId: string; activeId?: string }> {
  if (list.length === 0) return { selectedId: '' };
  try {
    const active = await getActive(domain);
    if (active && list.some((x) => x.id === active.id)) {
      return { selectedId: active.id, activeId: active.id };
    }
  } catch {
    // pas de config active pour ce domaine (404) → fallback
  }
  return { selectedId: list[0].id };
}

export const NewRunModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, preSelectedSchemaId }) => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [schemas, setSchemas] = useState<ExtractionSchema[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string>('');
  const [selectedPromptId, setSelectedPromptId] = useState<string>('');
  const [selectedSchemaId, setSelectedSchemaId] = useState<string>('');
  const [note, setNote] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [error, setError] = useState('');

  // Document selection
  const [groundTruthDocs, setGroundTruthDocs] = useState<GroundTruthDocument[]>([]);
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docTags, setDocTags] = useState<string[]>([]);

  // OCR and LLM configs
  const [ocrConfigs, setOcrConfigs] = useState<OCRConfig[]>([]);
  const [llmConfigs, setLlmConfigs] = useState<LLMConfig[]>([]);
  const [selectedOcrConfigId, setSelectedOcrConfigId] = useState<string>('');
  const [selectedLlmConfigId, setSelectedLlmConfigId] = useState<string>('');
  // Ids des configs ACTIVES du client+domaine (pour le marqueur ⭐). is_active sur
  // l'objet n'existe plus (vestige pré-multi-client) ; on garde l'id réel ici.
  const [activeIds, setActiveIds] = useState<{ prompt?: string; schema?: string; ocr?: string; llm?: string }>({});
  const [loadingOcrConfigs, setLoadingOcrConfigs] = useState(false);
  const [loadingLlmConfigs, setLoadingLlmConfigs] = useState(false);

  // Options
  const [storeLlmLogs, setStoreLlmLogs] = useState(true);

  // Charger les domaines et tags au montage
  useEffect(() => {
    if (isOpen) {
      loadDomains();
      loadTags();
    }
  }, [isOpen]);

  // Domaine courant, lu par les load* à la résolution de leurs fetchs. Garde
  // anti-course : StrictMode (dev) exécute l'effet 2×, et changer vite de
  // domaine peut faire arriver une réponse périmée — sans cette garde, un
  // setActiveIds({}) (branche else) ou une réponse d'un ancien domaine
  // écrasait les étoiles calculées (symptôme : ⭐ absentes au 1er chargement,
  // présentes seulement après changement de domaine).
  const currentDomainRef = useRef<string>('');

  // Charger les prompts, schémas, documents et configs quand le domaine change
  useEffect(() => {
    currentDomainRef.current = selectedDomain;
    if (selectedDomain) {
      loadPrompts(selectedDomain);
      loadSchemas(selectedDomain);
      loadGroundTruthDocs(selectedDomain);
      loadOcrConfigs(selectedDomain);
      loadLlmConfigs(selectedDomain);
    } else {
      setPrompts([]);
      setSchemas([]);
      setGroundTruthDocs([]);
      setOcrConfigs([]);
      setLlmConfigs([]);
      setSelectedPromptId('');
      setSelectedSchemaId('');
      setSelectedDocIds([]);
      setSelectedOcrConfigId('');
      setSelectedLlmConfigId('');
      setActiveIds({});
    }
  }, [selectedDomain]);

  // Synchroniser les sélections quand les listes sont chargées
  // NB : la présélection par défaut (config active du client+domaine) est gérée
  // directement dans load{Prompts,Schemas,OcrConfigs,LlmConfigs} via pickActiveId.
  // Pas de useEffect séparé ici — évite la double logique et le flag is_active mort.

  const loadDomains = async () => {
    try {
      const data = await domainService.list();
      setDomains(data);
      // Sélectionner le premier domaine par défaut
      if (data.length > 0 && !selectedDomain) {
        setSelectedDomain(data[0].name);
      }
    } catch (err) {
      console.error('Error loading domains:', err);
    }
  };

  const loadTags = async () => {
    try {
      const data = await benchmarkService.getTags();
      setAvailableTags(data);
    } catch (err) {
      console.error('Error loading tags:', err);
    }
  };

  const loadPrompts = async (domain: string) => {
    setLoadingPrompts(true);
    try {
      // Charger les prompts NON deprecated du domaine
      const data = await promptService.list({
        domain,
        include_deprecated: false
      });

      // Défaut = la config ACTIVE du client courant pour ce domaine (la même que
      // l'extraction prod utilise via ConfigResolver). L'ancien `find(is_active)`
      // était un vestige pré-multi-client : le flag is_active n'existe plus sur
      // ces objets → il tombait toujours sur data[0] (premier de la liste,
      // arbitraire). On interroge l'endpoint active-par-domaine ; fallback data[0].
      const picked = await pickActiveId(domain, data, (d) => promptService.getActive(d));
      if (currentDomainRef.current !== domain) return; // réponse périmée
      setPrompts(data);
      setSelectedPromptId(picked.selectedId);
      setActiveIds((prev) => ({ ...prev, prompt: picked.activeId }));
    } catch (err) {
      console.error('Error loading prompts:', err);
      setPrompts([]);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const loadSchemas = async (domain: string) => {
    setLoadingSchemas(true);
    try {
      // Charger les schémas NON deprecated du domaine
      const data = await schemaService.list({
        domain,
        include_deprecated: false
      });

      // Pré-sélectionner le schéma candidat si fourni, sinon la config ACTIVE du
      // client courant pour ce domaine (cf. note loadPrompts).
      const pickedSchema = await pickActiveId(domain, data, (d) => schemaService.getActive(d));
      if (currentDomainRef.current !== domain) return; // réponse périmée
      setSchemas(data);
      setActiveIds((prev) => ({ ...prev, schema: pickedSchema.activeId }));
      if (preSelectedSchemaId && data.find(s => s.id === preSelectedSchemaId)) {
        setSelectedSchemaId(preSelectedSchemaId);
      } else {
        setSelectedSchemaId(pickedSchema.selectedId);
      }
    } catch (err) {
      console.error('Error loading schemas:', err);
      setSchemas([]);
    } finally {
      setLoadingSchemas(false);
    }
  };

  const loadGroundTruthDocs = async (domain: string) => {
    setLoadingDocs(true);
    try {
      const data = await groundTruthService.list(domain);
      // Tags distincts → alimentent le filtre de la colonne Tags de la table.
      const tags = await groundTruthService.listDistinctTags(domain);
      if (currentDomainRef.current !== domain) return; // réponse périmée
      setGroundTruthDocs(data);
      // Présélection : tous les docs SAUF ceux marqués skip_benchmark (exclus
      // des benchmarks) — l'analyste ajuste ensuite via la table.
      setSelectedDocIds(data.filter(d => !d.skip_benchmark).map(d => d.id));
      setDocTags(tags);
    } catch (err) {
      console.error('Error loading ground truth documents:', err);
      setGroundTruthDocs([]);
      setDocTags([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadOcrConfigs = async (domain: string) => {
    setLoadingOcrConfigs(true);
    try {
      const data = await ocrConfigService.list(domain);
      // Défaut = config OCR ACTIVE du client courant pour ce domaine (cf. loadPrompts).
      const pickedOcr = await pickActiveId(domain, data, (d) => ocrConfigService.getActive(d));
      if (currentDomainRef.current !== domain) return; // réponse périmée
      setOcrConfigs(data);
      setSelectedOcrConfigId(pickedOcr.selectedId);
      setActiveIds((prev) => ({ ...prev, ocr: pickedOcr.activeId }));
    } catch (err) {
      console.error('Error loading OCR configs:', err);
      setOcrConfigs([]);
    } finally {
      setLoadingOcrConfigs(false);
    }
  };

  const loadLlmConfigs = async (domain: string) => {
    setLoadingLlmConfigs(true);
    try {
      const data = await llmConfigService.list(domain);
      // Défaut = config LLM ACTIVE du client courant pour ce domaine (cf. loadPrompts).
      const pickedLlm = await pickActiveId(domain, data, (d) => llmConfigService.getActive(d));
      if (currentDomainRef.current !== domain) return; // réponse périmée
      setLlmConfigs(data);
      setSelectedLlmConfigId(pickedLlm.selectedId);
      setActiveIds((prev) => ({ ...prev, llm: pickedLlm.activeId }));
    } catch (err) {
      console.error('Error loading LLM configs:', err);
      setLlmConfigs([]);
    } finally {
      setLoadingLlmConfigs(false);
    }
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDomain || !selectedPromptId || !selectedSchemaId) {
      setError('Veuillez sélectionner un domaine, un prompt et un schéma');
      return;
    }

    if (!selectedOcrConfigId || !selectedLlmConfigId) {
      setError('Veuillez sélectionner une config OCR et une config LLM');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const run = await benchmarkService.createRun({
        note,
        domain: selectedDomain,
        prompt_id: selectedPromptId,
        schema_id: selectedSchemaId,
        ocr_config_id: selectedOcrConfigId,
        llm_config_id: selectedLlmConfigId,
        tags,
        store_llm_logs: storeLlmLogs,
        document_ids: selectedDocIds.length > 0 ? selectedDocIds : undefined
      });

      // Execute the run immediately (don't await - fire and forget)
      benchmarkService.executeRun(run.id).catch(err => {
        console.error('Failed to execute run:', err);
      });

      onSuccess(run.id);
      resetForm();
    } catch (err: any) {
      console.error('Error creating run:', err);
      setError(err.message || 'Erreur lors de la création du run');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    // Réinitialise uniquement les champs saisis par l'utilisateur (note, tags…).
    // NE touche PAS à activeIds ni aux sélections dérivées du domaine : elles
    // reflètent la config active du domaine et sont rechargées par le
    // useEffect [selectedDomain]. Les vider ici cassait les étoiles à la
    // réouverture (selectedDomain inchangé → pas de rechargement).
    setNote('');
    setTags([]);
    setTagInput('');
    setError('');
    setStoreLlmLogs(true);
  };

  // Pont entre selectedDocIds (number[]) et le Set attendu par FilterableTable.
  // Les clés RESTENT numériques (rowKey renvoie doc.id: number) — surtout PAS de
  // conversion en string, sinon le Set ne matche plus rowKey et la sélection casse.
  const selectedDocKeys = new Set<string | number>(selectedDocIds);
  const handleDocSelectionChange = (keys: Set<string | number>) => {
    setSelectedDocIds(Array.from(keys, (k) => Number(k)));
  };

  // Docs affichés dans la table = tous SAUF ceux marqués skip_benchmark
  // (exclus des benchmarks — on ne les propose plus du tout à la sélection).
  const selectableDocs = groundTruthDocs.filter((d) => !d.skip_benchmark);

  // Tags de runs distincts (union des run_tags) → filterOptions de la colonne.
  const runTagOptions = Array.from(
    new Set(selectableDocs.flatMap((d) => d.run_tags || []))
  ).sort();

  // Colonnes de la mini-table de sélection des documents GT.
  const gtColumns: FilterableColumnDef<GroundTruthDocument>[] = [
    {
      key: 'filename',
      header: 'Nom du fichier',
      searchable: true,
      sortable: true,
      sortValueFn: (d) => d.filename,
      render: (d) => <span className="text-sm">{d.filename}</span>,
    },
    {
      key: 'tags',
      header: 'Tags',
      filterable: true,
      filterValueFn: (d) => d.tags || [],
      filterOptions: docTags.map((t) => ({ value: t, label: t })),
      render: (d) =>
        (d.tags || []).length > 0 ? (
          <span className="text-xs text-indigo-600">{(d.tags || []).join(', ')}</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      key: 'run_tags',
      header: 'Tags des runs',
      filterable: true,
      filterValueFn: (d) => d.run_tags || [],
      filterOptions: runTagOptions.map((t) => ({ value: t, label: t })),
      render: (d) =>
        (d.run_tags || []).length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {(d.run_tags || []).map((t) => (
              <span key={t} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      // Filtre pour ne voir que la sélection courante (ou l'inverse). Réutilise
      // le mécanisme de filtre standard ; filterValueFn lit selectedDocIds.
      key: 'selection',
      header: 'Sélection',
      filterable: true,
      filterValueFn: (d) => (selectedDocIds.includes(d.id) ? 'checked' : 'unchecked'),
      filterOptions: [
        { value: 'checked', label: 'Cochés' },
        { value: 'unchecked', label: 'Non cochés' },
      ],
      render: (d) =>
        selectedDocIds.includes(d.id) ? (
          <span className="text-xs text-green-700">✓ coché</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={() => { onClose(); resetForm(); }} title="Nouveau Benchmark Run" size="lg">
      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>
      )}

        <form onSubmit={handleSubmit}>
          {/* Sélection du domaine */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Domaine <span className="text-red-500">*</span>
            </label>
            <DomainSelector
              domains={domains}
              value={selectedDomain}
              onChange={(d) => setSelectedDomain(d || '')}
              showCounts
            />
          </div>

          {/* Sélection du prompt */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Prompt <span className="text-red-500">*</span>
            </label>
            {loadingPrompts ? (
              <div className="text-gray-500">Chargement des prompts...</div>
            ) : prompts.length === 0 ? (
              <div className="text-orange-600">
                Aucun prompt disponible pour ce domaine.
                <Link to="/admin/prompts" className="text-blue-600 ml-1 hover:underline">
                  Créer un prompt
                </Link>
              </div>
            ) : (
              <select
                value={selectedPromptId}
                onChange={(e) => setSelectedPromptId(e.target.value)}
                className="w-full border rounded px-3 py-2"
                disabled={!selectedDomain}
                required
              >
                {prompts.map((prompt) => (
                  <option key={prompt.id} value={prompt.id}>
                    {prompt.id === activeIds.prompt && '⭐ '}
                    {prompt.name} (v{prompt.version})
                  </option>
                ))}
              </select>
            )}
            {selectedPromptId && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedPromptId === activeIds.prompt
                  ? '⭐ Prompt actif du domaine'
                  : 'Prompt non actif'}
              </p>
            )}
          </div>

          {/* Sélection du schéma */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Schéma <span className="text-red-500">*</span>
            </label>
            {loadingSchemas ? (
              <div className="text-gray-500">Chargement des schémas...</div>
            ) : schemas.length === 0 ? (
              <div className="text-orange-600">
                Aucun schéma disponible pour ce domaine.
                <Link to="/admin/schemas" className="text-blue-600 ml-1 hover:underline">
                  Créer un schéma
                </Link>
              </div>
            ) : (
              <select
                value={selectedSchemaId}
                onChange={(e) => setSelectedSchemaId(e.target.value)}
                className="w-full border rounded px-3 py-2"
                disabled={!selectedDomain}
                required
              >
                {schemas.map((schema) => (
                  <option key={schema.id} value={schema.id}>
                    {schema.id === activeIds.schema && '⭐ '}
                    {schema.name} (v{schema.version})
                  </option>
                ))}
              </select>
            )}
            {selectedSchemaId && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedSchemaId === activeIds.schema
                  ? '⭐ Schéma actif du domaine'
                  : 'Schéma non actif'}
              </p>
            )}
          </div>

          {/* Sélection de la config OCR */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Config OCR <span className="text-red-500">*</span>
            </label>
            {loadingOcrConfigs ? (
              <div className="text-gray-500">Chargement des configs OCR...</div>
            ) : ocrConfigs.length === 0 ? (
              <div className="text-orange-600">
                Aucune config OCR disponible pour ce domaine.
                <Link to="/parametres/ocr" className="text-blue-600 ml-1 hover:underline">
                  Créer une config OCR
                </Link>
              </div>
            ) : (
              <select
                value={selectedOcrConfigId}
                onChange={(e) => setSelectedOcrConfigId(e.target.value)}
                className="w-full border rounded px-3 py-2"
                disabled={!selectedDomain}
                required
              >
                {ocrConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.id === activeIds.ocr && '⭐ '}
                    {config.name} (DPI {config.dpi}, PSM {config.psm})
                  </option>
                ))}
              </select>
            )}
            {selectedOcrConfigId && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedOcrConfigId === activeIds.ocr
                  ? '⭐ Config OCR active du domaine'
                  : 'Config OCR non active'}
              </p>
            )}
          </div>

          {/* Sélection de la config LLM */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Config LLM <span className="text-red-500">*</span>
            </label>
            {loadingLlmConfigs ? (
              <div className="text-gray-500">Chargement des configs LLM...</div>
            ) : llmConfigs.length === 0 ? (
              <div className="text-orange-600">
                Aucune config LLM disponible pour ce domaine.
                <Link to="/parametres/llm" className="text-blue-600 ml-1 hover:underline">
                  Créer une config LLM
                </Link>
              </div>
            ) : (
              <select
                value={selectedLlmConfigId}
                onChange={(e) => setSelectedLlmConfigId(e.target.value)}
                className="w-full border rounded px-3 py-2"
                disabled={!selectedDomain}
                required
              >
                {llmConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.id === activeIds.llm && '⭐ '}
                    {config.name} ({config.model_name})
                  </option>
                ))}
              </select>
            )}
            {selectedLlmConfigId && (
              <p className="text-xs text-gray-500 mt-1">
                {selectedLlmConfigId === activeIds.llm
                  ? '⭐ Config LLM active du domaine'
                  : 'Config LLM non active'}
              </p>
            )}
          </div>

          {/* Sélection des documents */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Documents à traiter
            </label>
            {loadingDocs ? (
              <div className="text-gray-500">Chargement des documents...</div>
            ) : selectableDocs.length === 0 ? (
              <div className="text-orange-600">
                Aucun document ground truth disponible pour ce domaine.
              </div>
            ) : (
              <>
                <div className="max-h-72 overflow-y-auto border rounded">
                  <FilterableTable
                    data={selectableDocs}
                    columns={gtColumns}
                    rowKey={(d) => d.id}
                    selectable
                    selectedKeys={selectedDocKeys}
                    onSelectionChange={handleDocSelectionChange}
                    keepSelectionOnFilter
                    defaultSort={{ key: 'filename', direction: 'asc' }}
                    noDataMessage="Aucun document ground truth disponible pour ce domaine."
                    emptyMessage="Aucun document ne correspond aux filtres."
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedDocIds.length} document(s) sélectionné(s)
                </p>
              </>
            )}
          </div>

          {/* Tags */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Tags</label>
            <div className="relative">
              <div className="flex gap-2 mb-2">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => {
                      setTagInput(e.target.value);
                      setShowTagSuggestions(true);
                    }}
                    onKeyDown={handleTagInputKeyDown}
                    onFocus={() => setShowTagSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Sélectionner ou saisir un tag (Enter pour ajouter)"
                  />
                  {showTagSuggestions && availableTags.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                      {availableTags
                        .filter(tag =>
                          tag.toLowerCase().includes(tagInput.toLowerCase()) &&
                          !tags.includes(tag)
                        )
                        .map(tag => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              if (!tags.includes(tag)) {
                                setTags([...tags, tag]);
                              }
                              setTagInput('');
                              setShowTagSuggestions(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100"
                          >
                            {tag}
                          </button>
                        ))}
                      {availableTags.filter(tag =>
                        tag.toLowerCase().includes(tagInput.toLowerCase()) &&
                        !tags.includes(tag)
                      ).length === 0 && tagInput && (
                        <div className="px-3 py-2 text-gray-500 text-sm">
                          Aucun tag trouvé. Appuyez sur Enter pour créer "{tagInput}"
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Ajouter
                </button>
              </div>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Note */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Note / Description</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border rounded px-3 py-2"
              rows={3}
              placeholder="Optionnel - Note ou description du run"
            />
          </div>

          {/* Option logs LLM */}
          <div className="mb-4">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="storeLlmLogs"
                checked={storeLlmLogs}
                onChange={(e) => setStoreLlmLogs(e.target.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <label htmlFor="storeLlmLogs" className="block text-sm font-medium cursor-pointer">
                  Stocker les logs LLM
                </label>
                <p className="text-xs text-gray-600">
                  Enregistrer les prompts et réponses complètes du LLM pour chaque document
                </p>
              </div>
            </div>
          </div>

          {/* Boutons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => { onClose(); resetForm(); }}
              className="px-4 py-2 border rounded hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !selectedPromptId || !selectedSchemaId || !selectedOcrConfigId || !selectedLlmConfigId || prompts.length === 0 || schemas.length === 0 || ocrConfigs.length === 0 || llmConfigs.length === 0}
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer le run'}
            </button>
          </div>
        </form>
    </Modal>
  );
};
